import { Component, inject, OnInit } from '@angular/core';
import {
    ClaimResponse,
    ClaimSearchRequest,
    IdLabel,
    LocalDateRange,
    PagedClaimSearchRequest,
    PagedResponseClaimResponse,
    SortablePagedRequestClaimSortBy,
} from '@core/generated/openapi';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { ButtonStyle, GridCoreService, Option } from 'rcp-common-lib';
import { EnumService } from '@services/enum/enum.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ClaimSearchService } from '@services/claim-search/claim-search.service';
import { BehaviorSubject, skip } from 'rxjs';
import { DEFAULT_CLAIM_PAGING } from '@core/constants/claim-search.constants';
import { TableService } from 'rcp-common-lib';
import { SortDirection } from '@angular/material/sort';
import { ColDef, GridOptions, RowClassRules } from 'ag-grid-community';
import { GridLoading, GridPagination, GridRowNavigation } from 'rcp-common-lib';
import { AuthService } from '@services/auth/auth.service';
import { PM_SYSTEM_IDS } from '@core/constants/external-system.constants';
import { STALLED_CLAIM_FILTER_IDS } from '@core/constants/stalled-claim-filter.constants';
import { ClaimStalledFilterService } from '@services/claim-stalled-filter/claim-stalled-filter.service';

@UntilDestroy()
@Component({
    selector: 'rcp-oup-claim-search',
    templateUrl: './claim-search.component.html',
    styleUrls: ['./claim-search.component.scss'],
    standalone: false,
})
export class ClaimSearchComponent implements OnInit {
    private readonly claimStalledFilterService = inject(
        ClaimStalledFilterService,
    );

    PAGED_CLAIMS: BehaviorSubject<PagedResponseClaimResponse> =
        new BehaviorSubject<PagedResponseClaimResponse>(null);

    paging: SortablePagedRequestClaimSortBy = JSON.parse(
        JSON.stringify(DEFAULT_CLAIM_PAGING),
    );

    filterLabel: string = 'Claim Search';

    searchForm: FormGroup;
    TODAY = new Date();
    statusOptions: Option[];

    currentSearch: PagedClaimSearchRequest;

    gridOptions: GridOptions =
        this.gridCoreService.getDefaultGridOptions(false);
    cols: ColDef[];
    dataSource: Array<ClaimResponse>;
    tableId: string;
    pagination: GridPagination = this.claimSearchService.defaultPagination;
    gridRowNavigation: GridRowNavigation =
        this.claimSearchService.defaultGridRowNavigation;
    isLoading: GridLoading = new BehaviorSubject<boolean>(true);
    rowClassRules: RowClassRules = this.claimSearchService.defaultRowClassRules;
    ignorePayer: boolean = true;
    noDataMessage: string;
    actionMenuRefresh: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
        false,
    );
    pmSystem: IdLabel;

    get pageSize() {
        return this.claimSearchService.getClaimPageSize();
    }

    get sortDirection(): SortDirection {
        return this.claimSearchService.mapSortDirectionRequestToResponse(
            this.paging.sortDirection,
        );
    }

    get sortActive() {
        return this.claimSearchService.mapSortNameRequestToResponse(
            this.paging.sortFieldName,
        );
    }

    hasSearchCriteria: boolean = false;

    constructor(
        private claimSearchService: ClaimSearchService,
        private router: Router,
        private route: ActivatedRoute,
        private enumService: EnumService,
        private tableService: TableService,
        private authService: AuthService,
        private gridCoreService: GridCoreService,
    ) {
        this.statusOptions = this.enumService
            .getOptionsFromEnum(ClaimSearchRequest.StatusListEnum)
            .filter(
                (option) =>
                    option.value !== ClaimSearchRequest.StatusListEnum.Unknown,
            );
    }

    ngOnInit(): void {
        this.pmSystem = this.authService.getUserPmSystem();

        this.claimSearchService.setClaimsCount(0);

        this.PAGED_CLAIMS.pipe(untilDestroyed(this)).subscribe((data) => {
            this.dataSource = data?.items;
            this.pagination.length = data?.totalCount;
            this.claimSearchService.setClaimsCount(data?.totalCount);
            this.pagination.pageIndex = data?.pageNumber - 1;

            this.setNoDataMessage();

            // Init table state
            this.tableId = this.tableService.generateTableId();
            this.tableService.initTableState(this.tableId);

            // Init grid cols
            this.cols = this.claimSearchService.getSearchCols(
                this.tableId,
                this.actionMenuRefresh,
            );
        });

        this.searchForm = this.claimSearchService.getSearchForm(
            this.currentSearch,
        );

        this.lookup();

        // Setup refresh for action menu
        this.actionMenuRefresh
            .pipe(untilDestroyed(this))
            .subscribe((refresh) => {
                if (refresh) {
                    this.onClaimChanged();
                }
            });

        //Listen to Location Filter changes
        this.authService.agingLocationFilter
            .pipe(untilDestroyed(this), skip(1))
            .subscribe((locations) => {
                //Maybe Change to Clear search fields and table instead?
                //Depends on desired behavior, this will research with new location
                this.search();
            });

        this.onStalledFilterChange();
    }

    lookup() {
        const storedSearch = sessionStorage?.getItem('claimSearch');

        if (storedSearch) {
            this.currentSearch = JSON.parse(storedSearch);
            this.searchForm.patchValue({
                claimNbr: this.currentSearch.pmsReferenceId,
                patientName: this.currentSearch.patientName,
                payorName: this.currentSearch.payorName,
                serviceDateFrom: this.currentSearch.serviceDateRange?.minDate,
                serviceDateTo: this.currentSearch.serviceDateRange?.maxDate,
                status: this.currentSearch.statusList,
            });
            this.search();
        }
    }

    storeSearch() {
        sessionStorage.setItem(
            'claimSearch',
            JSON.stringify(this.currentSearch),
        );
    }

    select(row: ClaimResponse): void {
        this.router.navigate(['detail/' + row.id], {
            relativeTo: this.route.parent,
        });
    }

    search() {
        this.hasSearchCriteria = true;
        this.isLoading.next(true);
        let search: PagedClaimSearchRequest = {
            pmsReferenceId: this.searchForm.controls['claimNbr'].getRawValue(),
            patientName: this.searchForm.controls['patientName'].getRawValue(),
            payorName: this.searchForm.controls['payorName'].getRawValue(),
            uploadAccountLocationIdIdList:
                this.authService.isAgingUploadByLocationOff(
                    this.authService.getAgingLocationFilter(),
                )
                    ? undefined
                    : this.authService.getAgingLocationFilter(),
            stalledReasonIsNull:
                this.claimStalledFilterService.getFilterStatusValue(
                    STALLED_CLAIM_FILTER_IDS.SEARCH_CLAIMS,
                )
                    ? null
                    : true,
        };
        let svcDateRange: LocalDateRange = {
            minDate: this.searchForm.controls['serviceDateFrom'].getRawValue(),
            maxDate: this.searchForm.controls['serviceDateTo'].getRawValue(),
        };
        if (svcDateRange.minDate || svcDateRange.maxDate) {
            search.serviceDateRange = svcDateRange;
        }

        if (this.searchForm.controls['status'].getRawValue()) {
            const selectedStatuses = this.searchForm.controls['status'].value;
            const statusList: ClaimSearchRequest.StatusListEnum[] = [];

            if (Array.isArray(selectedStatuses)) {
                selectedStatuses.forEach((value: string) => {
                    const statusEnumValue = this.mapToStatusEnum(value);
                    if (statusEnumValue !== null) {
                        statusList.push(statusEnumValue);
                    }
                });
            } else if (selectedStatuses) {
                const statusEnumValue = this.mapToStatusEnum(selectedStatuses);
                if (statusEnumValue !== null) {
                    statusList.push(statusEnumValue);
                }
            }
            search.statusList = statusList;
        }

        search.paged = this.getPaging();

        this.currentSearch = search;
        this.storeSearch();
        this.claimSearchService
            .searchClaimsPaged(search)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (claims) => {
                    this.PAGED_CLAIMS.next(claims);
                },
                error: (error) => {
                    console.error(error);
                },
            });
    }

    clearSearch() {
        this.tableService.deleteTableStateById(this.tableId);
        this.searchForm.reset();
        sessionStorage.setItem('claimSearch', null);
        this.claimSearchService.setClaimsCount(0);
        this.hasSearchCriteria = false;
        this.paging.pageNumber = 1;
        this.paging.sortDirection = DEFAULT_CLAIM_PAGING.sortDirection;
        this.paging.sortFieldName = DEFAULT_CLAIM_PAGING.sortFieldName;
        this.PAGED_CLAIMS.next(<PagedResponseClaimResponse>[]);
    }

    mapToStatusEnum(value: string): ClaimSearchRequest.StatusListEnum {
        switch (value) {
            case 'PENDING':
                return ClaimSearchRequest.StatusListEnum.Pending;
            case 'ACTIVE':
                return ClaimSearchRequest.StatusListEnum.Active;
            case 'REJECTED':
                return ClaimSearchRequest.StatusListEnum.Rejected;
            case 'DENIED':
                return ClaimSearchRequest.StatusListEnum.Denied;
            case 'UNDER_APPEAL':
                return ClaimSearchRequest.StatusListEnum.UnderAppeal;
            case 'COMPLETED':
                return ClaimSearchRequest.StatusListEnum.Completed;
            default:
                return null;
        }
    }

    pageChanged(event: any) {
        this.isLoading.next(true);
        this.claimSearchService.setClaimPageSize(event.pageSize);
        this.paging.pageNumber = event.pageIndex + 1;
        this.lookup();
    }

    sortChanged() {
        this.isLoading.next(true);
        this.lookup();
    }

    getPaging(): SortablePagedRequestClaimSortBy {
        const direction =
            this.claimSearchService.mapSortDirectionResponseToRequest(
                this.tableService.getTableState(this.tableId)?.sort?.direction,
            );
        this.paging.sortDirection = direction
            ? direction
            : this.paging.sortDirection;

        const field = this.claimSearchService.mapSortNameResponseToRequest(
            this.tableService.getTableState(this.tableId)?.sort?.active,
        );
        this.paging.sortFieldName = field ? field : this.paging.sortFieldName;

        // Make sure our saved sort matches current actual sort
        const fieldNameToSave =
            this.claimSearchService.mapSortNameRequestToResponse(
                this.paging.sortFieldName,
            );
        const directionToSave =
            this.claimSearchService.mapSortDirectionRequestToResponse(
                this.paging.sortDirection,
            );
        this.tableService.updateSort(
            this.tableId,
            fieldNameToSave,
            directionToSave,
        );

        // See if we have a saved pagination state, if so use that page number for the current view.
        const savedTable = this.tableService.getTableState(this.tableId);

        if (savedTable?.page && savedTable?.page?.pageIndex) {
            this.paging.pageNumber = savedTable.page.pageIndex + 1;
        }

        return {
            pageNumber: this.paging.pageNumber,
            sortFieldName: this.paging.sortFieldName,
            sortDirection: this.paging.sortDirection,
            unPaged: this.paging.unPaged,
            /* Use global page size */
            pageSize: this.claimSearchService.getClaimPageSize(),
        };
    }

    // Used to refresh data from cell renderers. Keep this.
    onClaimChanged() {
        this.tableService.deleteTableStateById(this.tableId);
        this.isLoading.next(true);
        this.lookup();
    }

    // Sets no data message.
    setNoDataMessage() {
        this.noDataMessage = this.claimSearchService.getNoDataMessage(
            this.ignorePayer,
            undefined,
            undefined,
        );
    }

    onStalledFilterChange() {
        this.claimStalledFilterService
            .onFilterStatusChange(STALLED_CLAIM_FILTER_IDS.SEARCH_CLAIMS)
            .pipe(untilDestroyed(this), skip(1))
            .subscribe({
                next: () => this.search(),
            });
    }

    protected readonly ButtonStyle = ButtonStyle;
    protected readonly PM_SYSTEM_IDS = PM_SYSTEM_IDS;
    protected readonly STALLED_CLAIM_FILTER_IDS = STALLED_CLAIM_FILTER_IDS;
}
